import { showToast } from "./toast";

// The app added to an iPhone's home screen can't download a file: iOS opens
// it in Quick Look over the app, with Open in Preview and no way back.
// `navigator.standalone` is iOS's own, true only there.
const homeScreen = (): boolean =>
  (navigator as { standalone?: boolean }).standalone === true;

/**
 * Save `file` (a card's image, the library's export). From an iPhone's home
 * screen it goes through the share sheet, whose Save to Files and Save Image
 * keep it and return to the app; elsewhere it downloads. Resolves to
 * "shared" or "saved", or null when the sheet was dismissed. A sheet refused
 * for want of a tap, as after a slow fetch, offers Save in a toast: a new tap.
 */
export const saveFile = async (
  file: File,
): Promise<"shared" | "saved" | null> => {
  if (
    homeScreen() &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] })
  ) {
    try {
      await navigator.share({ files: [file] });
    } catch (err) {
      if ((err as Error)?.name === "NotAllowedError")
        showToast(`${file.name} is ready`, {
          label: "Save",
          run: () => void saveFile(file),
        });
      return null;
    }
    return "shared";
  }
  const href = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = href;
  link.download = file.name;
  link.click();
  // The download has its bytes once it starts; a minute is ample.
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
  return "saved";
};
